import { useRef } from "react";

import type { BoxProps } from "@/ui/atoms/Box";
import Box from "@/ui/atoms/Box";
import TopBarOverview from "@/ui/molecules/TopBarOverview";
import LayoutFillParentWithFooter from "@/ui/layouts/LayoutFillParentWithFooter";

import type {
    TChartOfAccountsDefinition,
    TCompany,
    TNewCoADefinition,
} from "@/apis/odin/apiODIN";
import EditChartOfAccountsForm, {
    type CoAEditFormHandle,
} from "@/ui/organisms/coaEditForm/EditChartOfAccountsForm";
import { Button } from "@proda-ai/ui-kit";
import React from "react";

export type ViewEditChartOfAccountsDefinitionParams = {
    useState: ({ coaId }: { coaId: number }) => {
        excludedCoANames: string[];
        companies: TCompany[] | null;
        getCompaniesState: "idle" | "pending" | "rejected" | "resolved";
        getCoaState: "idle" | "pending" | "rejected" | "resolved";
        isCoaUpdatePending: boolean;
        isCoaUpdateError: boolean;
        coaDefinition: TChartOfAccountsDefinition | null;
        coaContainsNonOperatingAccounts: boolean;
        coaContainsSubcategories: boolean;
    };
    useCases: () => {
        onSubmit: (data: TNewCoADefinition, coaId?: number) => Promise<void>;
        getExcludedCoANames: () => Promise<void>;
        onDiscardCoA: (coaId?: number) => Promise<void>;
        navigateToChartOfAccountsEditDetails: (coaId: number) => void;
    };
};

export type ViewEditChartOfAccountsDefinitionProps = BoxProps & {
    coaId: number;
    navigateToDashboard: () => void;
};

export const createViewEditChartOfAccountsDefinition = ({
    useState,
    useCases,
}: ViewEditChartOfAccountsDefinitionParams) => {
    const ViewEditChartOfAccountsDefinition = ({
        coaId,
        navigateToDashboard,
        ...props
    }: ViewEditChartOfAccountsDefinitionProps) => {
        const {
            excludedCoANames,
            companies,
            getCompaniesState,
            coaDefinition,
            getCoaState,
            isCoaUpdatePending,
            isCoaUpdateError,
            coaContainsNonOperatingAccounts,
            coaContainsSubcategories,
        } = useState({ coaId });

        const {
            onSubmit,
            getExcludedCoANames,
            onDiscardCoA,
            navigateToChartOfAccountsEditDetails,
        } = useCases();

        const isLoading =
            getCoaState === "pending" ||
            isCoaUpdatePending ||
            getCompaniesState === "pending" ||
            getCoaState === "idle" ||
            getCompaniesState === "idle";

        const isError =
            getCoaState === "rejected" ||
            isCoaUpdateError ||
            getCompaniesState === "rejected";

        const coaIsDraft = coaDefinition?.isDraft ?? false;
        const formRef = useRef<CoAEditFormHandle>(null);

        const [isDirty, setIsDirty] = React.useState(false);

        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    ...(props.sx || {}),
                }}
                {...props}
            >
                <TopBarOverview
                    title={coaDefinition?.name}
                    backButtonLabel={"Back to Charts of Accounts list"}
                    onBackButtonClick={() => {
                        navigateToDashboard();
                    }}
                />

                <LayoutFillParentWithFooter
                    footerContent={
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                padding: "1rem",
                            }}
                        >
                            {coaIsDraft && (
                                <Button
                                    onClick={() => {
                                        void onDiscardCoA(coaId);
                                    }}
                                    size="large"
                                    disabled={isLoading || isError}
                                    pattern="danger"
                                    style={{ marginRight: "1rem" }}
                                >
                                    Discard Draft
                                </Button>
                            )}
                            {!isDirty ? (
                                <Button
                                    onClick={() => {
                                        navigateToChartOfAccountsEditDetails(
                                            coaId,
                                        );
                                    }}
                                    size="large"
                                    disabled={isLoading || isError}
                                >
                                    Next
                                </Button>
                            ) : (
                                <Button
                                    onClick={() => {
                                        void formRef.current?.onConfirm();
                                    }}
                                    size="large"
                                    disabled={isLoading || isError}
                                >
                                    Save Changes
                                </Button>
                            )}
                        </Box>
                    }
                >
                    <EditChartOfAccountsForm
                        ref={formRef}
                        excludedCoANames={excludedCoANames}
                        companies={companies}
                        getCompaniesState={getCompaniesState}
                        coaDefinition={coaDefinition}
                        coaContainsNonOperatingAccounts={
                            coaContainsNonOperatingAccounts
                        }
                        coaContainsSubcategories={coaContainsSubcategories}
                        getExcludedCoANames={getExcludedCoANames}
                        onCoASubmit={onSubmit}
                        onDiscardCoA={() => {
                            void onDiscardCoA(coaId);
                        }}
                        navigateToChartOfAccountsEditDetails={
                            navigateToChartOfAccountsEditDetails
                        }
                        coaId={coaId}
                        isLoading={isLoading}
                        isError={isError}
                        onDirtyChange={setIsDirty}
                    />
                </LayoutFillParentWithFooter>
            </Box>
        );
    };
    return ViewEditChartOfAccountsDefinition;
};
